import React, { useMemo, useEffect } from "react";

import "./standard_role_selection_card.less";
import { CardLabel } from "@src/components/card_label/index";
import { setSelectedStandardRoleId } from "@src/services/resourcePlanModalSlice";
import { useGetStandardRolesQuery } from "@src/services/slices/standardRolesApi";
import { useAppDispatch } from "@src/setupStore";
import { StandardRole } from "@src/types";
import { Form, Select, Card, Button, Popover, Tooltip, Row, Col } from "antd";
import { DefaultOptionType } from "antd/es/select";
import _ from "lodash";

export interface StandardRoleSelectionCardProps {
  basicRoleName: string;
  roleSpecificationName: string;
  standardRoleName: string;
}

type StandardRoleSelectionFormValues = {
  basicRole: string;
  roleSpecification: string;
};

const StandardRoleSelectionCard: React.FC<StandardRoleSelectionCardProps> = ({
  basicRoleName,
  roleSpecificationName,
  standardRoleName,
}) => {
  const dispatch = useAppDispatch();
  const initialFormValues: StandardRoleSelectionFormValues = {
    basicRole: basicRoleName,
    roleSpecification: getDisplayedRoleSpecificationName(roleSpecificationName),
  };

  // States
  const [basicRoleRelatedStandardRoles, setBasicRoleRelatedStandardRoles] =
    React.useState<StandardRole[]>([]);
  const [selectedStandardRole, setSelectedStandardRole] =
    React.useState<StandardRole | null>(null);

  // hooks
  const { data: standardRoles } = useGetStandardRolesQuery();
  const [form] = Form.useForm<StandardRoleSelectionFormValues>();

  const standardRolesMap: Map<string, StandardRole[]> = useMemo(() => {
    const visibleRoles: StandardRole[] = (standardRoles ?? []).filter(
      (role: StandardRole) =>
        !role.onlyVisibleForAdmin && role.basicRole?.basicRoleName
    );

    return new Map(
      Object.entries(
        _.groupBy(
          visibleRoles,
          (role: StandardRole) => role.basicRole?.basicRoleName
        )
      )
    );
  }, [standardRoles]);

  useEffect(() => {
    if (!standardRoles || (!standardRoleName && !basicRoleName)) return;

    const currentSelectedStandardRole: StandardRole = standardRoles.find(
      (standardRole: StandardRole) =>
        standardRole.standardRoleName === standardRoleName
    );

    setSelectedStandardRole(currentSelectedStandardRole);
    setBasicRoleRelatedStandardRoles(standardRolesMap.get(basicRoleName));
  }, [standardRoles, standardRoleName, basicRoleName]);

  /**
   * Get basic role options
   *
   * @returns {DefaultOptionType[]} - the default options type array for the selection
   */
  function getBasicRoleOptions(): DefaultOptionType[] {
    if (!standardRolesMap) return [];

    const basicRoleNames: string[] = Array.from(standardRolesMap.keys());

    return basicRoleNames.map((basicRoleName: string) => ({
      value: basicRoleName,
      label: basicRoleName,
    }));
  }

  /**
   * Get role specification options
   *
   * @returns {DefaultOptionType[]} - the default options type array for the selection
   */
  function getRoleSpecificationOptions(): DefaultOptionType[] {
    return basicRoleRelatedStandardRoles?.map((standardRole: StandardRole) => ({
      value: standardRole?.roleSpecification?.roleSpecificationId,
      label: getRoleSpecificationLabel(standardRole),
      disabled: !standardRole?.teamLead,
    }));
  }

  /**
   * Get role specification label. Show tooltip if no team lead is assigned.
   *
   * @param {StandardRole} standardRole - Standard role
   *
   * @returns {React.ReactElement} - the role specification tooltip with the label.
   */
  function getRoleSpecificationLabel(
    standardRole: StandardRole
  ): React.ReactElement {
    return (
      <Tooltip
        title={
          !standardRole?.teamLead &&
          "No team lead assigned to the role, contact administrator for further details"
        }
      >
        {getDisplayedRoleSpecificationName(
          standardRole?.roleSpecification?.roleSpecificationName
        )}
      </Tooltip>
    );
  }

  /**
   * Get displayed role specification name
   *
   * @param {string} roleSpecificationName - Role specification name
   *
   * @return {string} - The displayed role specification name or none when no available
   */
  function getDisplayedRoleSpecificationName(
    roleSpecificationName: string
  ): string {
    return roleSpecificationName || "--None--";
  }

  /**
   * Handle change of basic role. If only one role specification is available than the role specification will be auto selected.
   * If there is no role specification than role specification will be disabled with the auto selected --none-- option.
   *
   * @param {string} selectedBasicRoleName - selected basic role name
   */
  function onChangeBasicRole(selectedBasicRoleName: string): void {
    setSelectedStandardRole(null);

    const standardRoles: StandardRole[] = standardRolesMap.get(
      selectedBasicRoleName
    );
    setBasicRoleRelatedStandardRoles(standardRoles);

    if (standardRoles.length === 1 && !standardRoles[0].roleSpecification) {
      // if there is only one role or no role specification than it should be selected automatically
      const selectedStandardRole: StandardRole = standardRoles[0];

      form.setFieldsValue({
        roleSpecification: getDisplayedRoleSpecificationName(
          selectedStandardRole?.roleSpecification?.roleSpecificationName
        ),
      });

      form.validateFields(["roleSpecification"]).then(() => {
        setSelectedStandardRole(selectedStandardRole);
        dispatch(
          setSelectedStandardRoleId(selectedStandardRole.standardRoleId)
        );
      });
    } else {
      // should reset role specification if there are multiple roles to select
      form.setFieldsValue({ roleSpecification: undefined });
      dispatch(setSelectedStandardRoleId(null));
    }
  }

  /**
   * Handle change of role specification
   *
   * @param {number} roleSpecificationId - the selected role specification id
   *
   */
  function onChangeRoleSpecification(roleSpecificationId: number): void {
    const selectedRole: StandardRole = basicRoleRelatedStandardRoles.find(
      (standardRole: StandardRole) =>
        standardRole?.roleSpecification?.roleSpecificationId ===
        roleSpecificationId
    );

    setSelectedStandardRole(selectedRole);
    dispatch(setSelectedStandardRoleId(selectedRole.standardRoleId));
  }

  /**
   * Check if role specification is disabled
   *
   * @returns {boolean} - will return true if the role specification is disabled or not
   */
  function isRoleSpecificationDisabled(): boolean {
    return (
      !basicRoleRelatedStandardRoles ||
      basicRoleRelatedStandardRoles?.length === 0 ||
      (basicRoleRelatedStandardRoles?.length === 1 &&
        !basicRoleRelatedStandardRoles[0].roleSpecification)
    );
  }

  return (
    <Card title="Role" className="standard-role-selection-card info-card">
      {/* <!-- Basic Role Selection--> */}
      <Form form={form} layout="vertical" initialValues={initialFormValues}>
        <Form.Item label="Standard role" name="basicRole">
          <Select
            showSearch
            placeholder="Select a basic role"
            onChange={onChangeBasicRole}
            options={getBasicRoleOptions()}
            size="large"
          />
        </Form.Item>

        {/* <!-- Role Specification Selection--> */}
        <Form.Item label="Role Specification" name="roleSpecification">
          <Select
            showSearch
            disabled={isRoleSpecificationDisabled()}
            onChange={onChangeRoleSpecification}
            placeholder="Select a specific role"
            options={getRoleSpecificationOptions()}
            size="large"
          />
        </Form.Item>
      </Form>

      {/* <!-- Team Lead and Description--> */}
      <Row justify="space-between">
        <Col>
          {selectedStandardRole?.teamLead && (
            <CardLabel label="Responsible Team lead">
              {selectedStandardRole?.teamLead?.fullName}
            </CardLabel>
          )}
        </Col>
        <Col>
          <Popover
            trigger="click"
            placement="right"
            rootClassName="standard-role-selection-card__role-info"
            title={selectedStandardRole?.standardRoleName}
            content={selectedStandardRole?.description || "No description"}
          >
            <Button disabled={!selectedStandardRole} size="large">
              Show Description
            </Button>
          </Popover>
        </Col>
      </Row>
    </Card>
  );
};

export default StandardRoleSelectionCard;
